import { Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { formatHours, TOOLTIP_STYLE, toKey, toNumber } from './chart-theme';
import { useChartAnimation } from './use-chart-animation';

export interface LineSeries {
  key: string;
  label: string;
  color: string;
  values: number[];
}

/**
 * Labelled multi-series line chart with a month axis and a tooltip — the
 * fuller counterpart to {@link TrendLines}, for when the reader needs to
 * identify a specific month rather than just see the shape.
 */
export function MultiLineTrend({
  labels,
  series,
  height = 130,
}: {
  labels: string[];
  series: LineSeries[];
  height?: number;
}) {
  const animation = useChartAnimation();
  const data = labels.map((label, i) => {
    const row: Record<string, string | number> = { label };
    for (const s of series) row[s.key] = s.values[i] ?? 0;
    return row;
  });
  const labelFor = new Map(series.map((s) => [s.key, s.label]));

  return (
    <div style={{ width: '100%', height }}>
      <ResponsiveContainer>
        <LineChart data={data} margin={{ top: 6, right: 8, bottom: 0, left: 8 }}>
          <XAxis
            dataKey="label"
            interval={1}
            tickLine={false}
            axisLine={false}
            tick={{ fontSize: 9.5, fill: 'var(--muted-foreground)' }}
          />
          <YAxis hide domain={[0, 'dataMax']} />
          <Tooltip
            contentStyle={TOOLTIP_STYLE}
            formatter={(value, key) => [
              `${formatHours(toNumber(value))} hrs`,
              labelFor.get(toKey(key)) ?? toKey(key),
            ]}
          />
          {series.map((s) => (
            <Line
              key={s.key}
              type="monotone"
              dataKey={s.key}
              stroke={s.color}
              strokeWidth={2}
              dot={false}
              {...animation}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
